import React from 'react';
import { Link } from 'react-router-dom';
import { Terminal } from 'lucide-react';
import { SERVICES } from '../../constants';

/* No entrance animation to avoid CLS; footer is visible from first paint */

function FooterLink({
  to,
  children,
  className = '',
}: {
  to: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <li>
      <Link
        to={to}
        className={`group inline-block py-2 min-h-[44px] flex items-center hover:text-blue-600 transition-colors duration-300 relative ${className}`}
      >
        <span className="relative inline-block">
          {children}
          <span className="absolute left-0 bottom-0 w-0 h-px bg-blue-600 group-hover:w-full transition-all duration-300 ease-out" aria-hidden />
        </span>
      </Link>
    </li>
  );
}

const Footer: React.FC = () => (
    <footer className="relative bg-white border-t border-slate-200/80 text-slate-600 font-sans overflow-hidden min-h-[18rem]" style={{ contain: 'layout' }}>
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent" aria-hidden />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 relative">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-4 md:gap-6">
          <div className="sm:col-span-2 md:col-span-1">
            <Link
              to="/"
              className="group flex items-center gap-2 mb-2 w-fit min-h-[44px] min-w-[44px] items-center"
              aria-label="Infraverge home"
            >
              <span className="flex items-center justify-center rounded-lg bg-blue-600 text-white p-1.5 shrink-0">
                <Terminal size={18} className="text-white" strokeWidth={2.25} />
              </span>
              <span className="text-base sm:text-lg font-bold text-slate-900 font-mono tracking-wide">
                INFRAVERGE
              </span>
            </Link>
            <p className="text-sm leading-relaxed text-slate-600">
              Building the autonomous intelligence infrastructure for the enterprise of tomorrow.
            </p>
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-2 font-mono">
              Company
            </h3>
            <ul className="space-y-0.5 text-sm" role="list">
              <FooterLink to="/about">About Us</FooterLink>
              <FooterLink to="/careers">Careers</FooterLink>
              <FooterLink to="/insights">Insights</FooterLink>
              <FooterLink to="/contact">Contact</FooterLink>
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-2 font-mono">
              Services
            </h3>
            <ul className="space-y-0.5 text-sm" role="list">
              {SERVICES.map((s) => (
                <FooterLink key={s.id} to={`/services/${s.slug}`}>
                  {s.title}
                </FooterLink>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-2 font-mono">
              Legal
            </h3>
            <ul className="space-y-0.5 text-sm" role="list">
              <FooterLink to="/privacy">Privacy Policy</FooterLink>
              <FooterLink to="/terms">Terms & Conditions</FooterLink>
              <FooterLink to="/imprint">Imprint</FooterLink>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200/80 mt-6 sm:mt-8 pt-4 sm:pt-6 flex flex-col md:flex-row justify-between items-center gap-2 text-xs text-slate-600 text-center md:text-left">
          <p>&copy; {new Date().getFullYear()} Infraverge GmbH. All rights reserved.</p>
          <p className="font-mono text-slate-600">Designed for scalability.</p>
        </div>
      </div>
    </footer>
  );

export default Footer;
